<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

import type { IBhs } from '@/modules/settings/interfaces/BusinessHours';
import ButtonG from '@/modules/shared/components/ButtonComponent.vue';
import useModal from '@/modules/shared/composable/useModal';
import businessHoursC from '@/modules/settings/components/businessHours';
import useCompanyBusinessHours from '@/modules/settings/composables/useCompanyBusinessHours';
import { CheckCircleIcon } from '@heroicons/vue/20/solid';
import ToastDemoComponent from '@/modules/shared/components/ToastDemoComponent.vue';

const { hideModal } = useModal();
const { businessHours, createCompanyBusinessHours, deleteCompanyBusinessHours, getCompanyBusinessHours } = useCompanyBusinessHours();
const toast = useToast();

const editBusinessData = ref<IBhs>({
    timezone: businessHours.value.timezone,
    businessHours: businessHours.value.businessHours
});

watch(businessHours, (newBusinessHours) => {
    editBusinessData.value.timezone = newBusinessHours.timezone;
    if (newBusinessHours.businessHours) editBusinessData.value.businessHours = newBusinessHours.businessHours;
});

const save = () => {
    if (editBusinessData.value.businessHours.length) {
        const newBusinessHours = {
            business_hours: editBusinessData.value.businessHours.map((bsh) => {
                return { weekday: bsh.id.slice(0, 3), start_time: `${bsh.start_time}:00`, end_time: `${bsh.end_time}:00` };
            }),
            timezone: editBusinessData.value.timezone
        };

        createCompanyBusinessHours(newBusinessHours).then(async () => {
            await getCompanyBusinessHours();
            hideModal();
            toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
        });
    } else {
        deleteCompanyBusinessHours().then(async () => {
            await getCompanyBusinessHours();
            hideModal();
            toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
        });
    }
};
</script>

<template>
    <div class="mt-6 flex gap-8">
        <div class="basis-2/5 min-[1580px]:basis-2/6">
            <p class="text-sm leading-5 font-medium text-gray-900">Company Business Hours</p>
            <p class="text-sm leading-5 font-normal text-gray-500 mt-2">
                Define the hours that your team is expected to be available to respond to emails. Hours that fall outside this schedule will not count towards response times.
            </p>
        </div>
        <div class="basis-3/5 min-[1580px]:basis-4/6 bg-white rounded-md shadow-sm">
            <div class="p-6">
                <businessHoursC v-model:days="editBusinessData.businessHours" v-model:timezone="editBusinessData.timezone" :data="editBusinessData" />
            </div>

            <div class="py-3 px-6 flex justify-end border-t border-gray-200">
                <ButtonG text="Save" @Click="save" />
            </div>
        </div>
    </div>
</template>

<style scoped></style>
